
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Edit3,
  Loader2,
  Database,
  RefreshCw,
  Building2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  Eye,
  Clock3,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
});

const PER_PAGE = 10;

const DB_TYPES = ["MASTER", "INVENTORY", "PAYROLL", "TALLY"];

function getStatus(row) {
  return String(row?.EffectiveStatus || "UNKNOWN").toUpperCase();
}

function isSuspended(row) {
  return (
    row?.IsSuspended === true ||
    row?.IsSuspended === 1 ||
    row?.IsSuspended === "1" ||
    getStatus(row) === "BLOCKED"
  );
}

function getStatusClass(status) {
  if (status === "ACTIVE") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  }

  if (status === "BLOCKED" || status === "SUSPENDED") {
    return "bg-rose-50 text-rose-700 ring-rose-200";
  }

  if (status === "EXPIRED") {
    return "bg-orange-50 text-orange-700 ring-orange-200";
  }

  return "bg-amber-50 text-amber-700 ring-amber-200";
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function getProvisioningStatus(summary) {
  if (!summary) return "NOT CONFIGURED";

  const total = Number(summary.DatabaseCount || 0);
  const ready = Number(summary.ReadyCount || 0);
  const failed = Number(summary.FailedCount || 0);

  if (failed > 0) return "FAILED";
  if (total === 4 && ready === 4) return "READY";

  return "PENDING";
}

function getProvisioningClass(status) {
  switch (status) {
    case "READY":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";

    case "FAILED":
      return "bg-rose-50 text-rose-700 ring-rose-200";

    case "PENDING":
      return "bg-amber-50 text-amber-700 ring-amber-200";

    default:
      return "bg-slate-100 text-slate-600 ring-slate-200";
  }
}

function SuspendToggle({ row, pendingId, onToggle }) {
  const suspended = isSuspended(row);
  const checked = !suspended;
  const disabled = pendingId !== null;

  return (
    <label className="relative inline-flex cursor-pointer select-none items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(row)}
        aria-label={
          checked ? "Suspend tenant license" : "Activate tenant license"
        }
      />

      <span
        className={`relative h-6 w-12 rounded-full transition-colors duration-200 ${
          checked ? "bg-emerald-500" : "bg-rose-500"
        } ${
          disabled ? "opacity-60" : ""
        } peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-400 peer-focus-visible:ring-offset-2`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? "translate-x-6" : ""
          }`}
        />
      </span>

      <span
        className={`ml-2 inline-flex items-center gap-1 text-xs font-semibold ${
          checked ? "text-emerald-700" : "text-rose-700"
        }`}
      >
        {pendingId === row.TenantId ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Updating...
          </>
        ) : checked ? (
          "Active"
        ) : (
          "Suspended"
        )}
      </span>
    </label>
  );
}

function ProvisioningBadge({ summary }) {
  const status = getProvisioningStatus(summary);

  if (!summary) {
    return (
      <span className="text-xs text-slate-400">
        Not configured
      </span>
    );
  }

  const total = Number(summary.DatabaseCount || 0);
  const ready = Number(summary.ReadyCount || 0);

  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${getProvisioningClass(
          status
        )}`}
      >
        {status}
      </span>

      <span className="text-xs text-slate-500">
        {ready} / {total} databases ready
      </span>
    </div>
  );
}

function DatabaseDetailsModal({ tenant, details, loading, error, onClose }) {
  if (!tenant) return null;

  const databases = details?.databases || [];
  const company = details?.company || tenant;

  const databaseMap = {};

  databases.forEach((item) => {
    databaseMap[item.DatabaseType] = item;
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Tenant database details"
        className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 bg-emerald-700 px-5 py-4 text-white">
          <div>
            <h2 className="text-lg font-bold">
              Database Configuration
            </h2>
            <p className="mt-1 text-sm text-emerald-100">
              {company.Name || tenant.Name} — {tenant.TenantCode}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-sm font-semibold hover:bg-white/20"
          >
            Close
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-14 text-slate-500">
              <Loader2 size={20} className="animate-spin text-emerald-600" />
              Loading database details...
            </div>
          ) : error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Company</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">
                    {company.LegalName || company.Name || "—"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">License Plan</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">
                    {company.PlanCode || tenant.PlanCode || "—"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Databases</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">
                    {databases.length} / 4 configured
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {DB_TYPES.map((type) => {
                  const item = databaseMap[type];
                  const status = String(
                    item?.ProvisioningStatus || "NOT CONFIGURED"
                  ).toUpperCase();

                  return (
                    <div
                      key={type}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Database size={17} className="text-emerald-600" />
                          <span className="text-sm font-bold text-slate-800">
                            {type}
                          </span>
                        </div>

                        <span
                          className={`rounded-full px-2 py-1 text-[11px] font-semibold ring-1 ${getProvisioningClass(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </div>

                      <p className="break-all font-mono text-xs text-slate-700">
                        {item?.DatabaseName || "Not configured"}
                      </p>

                      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span>
                          FY: {item?.FinancialYear || "Not applicable"}
                        </span>

                        {status === "READY" ? (
                          <CheckCircle2 size={16} className="text-emerald-600" />
                        ) : status === "FAILED" ? (
                          <XCircle size={16} className="text-rose-600" />
                        ) : (
                          <Clock3 size={16} className="text-amber-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                Database names are registered in the Supreme Admin
                database. Actual database provisioning will be
                completed after the templates are imported.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tenants_List() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [databaseSummaries, setDatabaseSummaries] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const loadTenants = useCallback(async (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError("");

    try {
      const [tenantResponse, databaseResponse] = await Promise.all([
        api.get("/api/tenants"),
        api.get("/api/tenants/infra-databases"),
      ]);

      const data = Array.isArray(tenantResponse.data)
        ? tenantResponse.data
        : Array.isArray(tenantResponse.data?.rows)
          ? tenantResponse.data.rows
          : Array.isArray(tenantResponse.data?.data)
            ? tenantResponse.data.data
            : [];

      const databaseData = Array.isArray(databaseResponse.data?.rows)
        ? databaseResponse.data.rows
        : [];

      const summaries = {};

      databaseData.forEach((item) => {
        summaries[item.TenantId] = item;
      });

      setRows(data);
      setDatabaseSummaries(summaries);

      return true;
    } catch (err) {
      console.error("Failed to load tenants:", err);
      setError(getErrorMessage(err, "Unable to load tenant records."));
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.TenantCode,
        row.Name,
        row.DatabaseName,
        row.PlanCode,
        row.EffectiveStatus,
        getProvisioningStatus(databaseSummaries[row.TenantId]),
      ].some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      )
    );
  }, [rows, search, databaseSummaries]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PER_PAGE)
  );

  const currentPage = Math.min(page, totalPages);
  const first = (currentPage - 1) * PER_PAGE;

  const currentRows = useMemo(
    () => filtered.slice(first, first + PER_PAGE),
    [filtered, first]
  );

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const toggleSuspend = async (row) => {
    if (pendingId !== null) return;

    const currentlySuspended = isSuspended(row);

    // Prevent activation until all four databases are READY.
    if (currentlySuspended) {
      const summary = databaseSummaries[row.TenantId];

      if (summary) {
        const total = Number(summary.DatabaseCount || 0);
        const ready = Number(summary.ReadyCount || 0);

        if (total !== 4 || ready !== 4) {
          await Swal.fire({
            icon: "warning",
            title: "Provisioning Pending",
            text:
              "All four Infra V5 databases must be READY before activating this tenant.",
            confirmButtonColor: "#059669",
          });

          return;
        }
      }
    }

    const action = currentlySuspended ? "reactivate" : "suspend";

    const confirm = await Swal.fire({
      title: currentlySuspended
        ? "Reactivate License?"
        : "Suspend License?",
      text: `Are you sure you want to ${action} the license for ${
        row.Name || row.TenantCode
      }?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: currentlySuspended
        ? "Yes, Reactivate"
        : "Yes, Suspend",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#059669",
    });

    if (!confirm.isConfirmed) return;

    setPendingId(row.TenantId);

    try {
      const response = await api.post(
        "/api/license/toggle-suspend",
        {
          tenantId: row.TenantId,
        }
      );

      if (
        response.data?.ok === false ||
        response.data?.success === false
      ) {
        throw new Error(
          response.data?.message ||
            response.data?.error ||
            "Unable to update license."
        );
      }

      const reloaded = await loadTenants(false);

      if (!reloaded) {
        throw new Error(
          "License updated, but the tenant list could not be refreshed."
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        text: currentlySuspended
          ? "Tenant license reactivated successfully."
          : "Tenant license suspended successfully.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Toggle suspend error:", err);

      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: getErrorMessage(
          err,
          "Unable to update tenant license."
        ),
        confirmButtonColor: "#059669",
      });
    } finally {
      setPendingId(null);
    }
  };

  const goEdit = (row) => {
    navigate(`/supremeadmin/tenantEdit/${row.TenantId}`, {
      state: { row },
    });
  };

  const openDatabaseDetails = async (row) => {
    setSelectedTenant(row);
    setTenantDetails(null);
    setDetailsError("");
    setDetailsLoading(true);

    try {
      const response = await api.get(
        `/api/tenants/${row.TenantId}/infra-details`
      );

      if (
        response.data?.ok === false ||
        response.data?.success === false
      ) {
        throw new Error(
          response.data?.message || "Unable to fetch tenant details."
        );
      }

      setTenantDetails(response.data);
    } catch (err) {
      console.error("Database details error:", err);
      setDetailsError(
        getErrorMessage(err, "Unable to load database details.")
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const totalTenants = rows.length;

  const activeTenants = rows.filter(
    (row) => getStatus(row) === "ACTIVE"
  ).length;

  const suspendedTenants = rows.filter(
    (row) => isSuspended(row)
  ).length;

  const pendingTenants = rows.filter(
    (row) =>
      getProvisioningStatus(
        databaseSummaries[row.TenantId]
      ) === "PENDING"
  ).length;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex min-h-screen flex-col md:flex-row">
        <SuperAdminNav />

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <Database size={22} />
              </div>

              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  Tenant Management
                </h1>
                <p className="text-sm text-slate-500">
                  Manage Infra V5 companies, databases and licenses
                </p>
              </div>
            </div>

            <Time />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Total Tenants"
              value={totalTenants}
              icon={Building2}
              color="slate"
            />

            <SummaryCard
              label="Active Tenants"
              value={activeTenants}
              icon={ShieldCheck}
              color="emerald"
            />

            <SummaryCard
              label="Suspended Licenses"
              value={suspendedTenants}
              icon={AlertCircle}
              color="rose"
            />

            <SummaryCard
              label="Pending Provisioning"
              value={pendingTenants}
              icon={Clock3}
              color="amber"
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/supremeadmin/tenant_Add"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <Plus size={17} />
                Add New Tenant
              </Link>

              <button
                type="button"
                onClick={() => loadTenants(false)}
                disabled={loading || refreshing}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw
                  size={16}
                  className={refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </div>

            <div className="relative w-full sm:w-96">
              <Search
                size={17}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={search}
                onChange={handleSearch}
                placeholder="Search tenant, code, database, plan..."
                className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
              />
            </div>
          </div>

          {error && (
            <div className="mt-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />

              <div className="flex-1">
                <p className="font-semibold">
                  Unable to Load Tenants
                </p>
                <p className="mt-1">{error}</p>
              </div>

              <button
                type="button"
                onClick={() => loadTenants()}
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
              >
                Retry
              </button>
            </div>
          )}

          <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-emerald-700 text-white">
                  <tr>
                    <TableHead>#</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Tenant Name</TableHead>
                    <TableHead>Master Database</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>License Status</TableHead>
                    <TableHead>Provisioning</TableHead>
                    <TableHead>Suspend</TableHead>
                    <TableHead>Action</TableHead>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-14 text-center"
                      >
                        <div className="flex items-center justify-center gap-2 text-slate-500">
                          <Loader2
                            size={20}
                            className="animate-spin text-emerald-600"
                          />
                          Loading tenant records...
                        </div>
                      </td>
                    </tr>
                  ) : currentRows.length > 0 ? (
                    currentRows.map((row, index) => {
                      const status = getStatus(row);
                      const summary =
                        databaseSummaries[row.TenantId];

                      return (
                        <tr
                          key={row.TenantId}
                          className="odd:bg-white even:bg-slate-50 hover:bg-emerald-50/40"
                        >
                          <TableCell className="text-slate-500">
                            {first + index + 1}
                          </TableCell>

                          <TableCell className="font-semibold text-slate-900">
                            {row.TenantCode || "—"}
                          </TableCell>

                          <TableCell>
                            {row.Name || "—"}
                          </TableCell>

                          <TableCell>
                            <span className="font-mono text-xs text-slate-700">
                              {row.DatabaseName || "—"}
                            </span>
                          </TableCell>

                          <TableCell>
                            <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                              Infra V5
                            </span>
                          </TableCell>

                          <TableCell>
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${getStatusClass(
                                status
                              )}`}
                            >
                              {status}
                            </span>
                          </TableCell>

                          <TableCell>
                            <ProvisioningBadge summary={summary} />
                          </TableCell>

                          <TableCell>
                            <SuspendToggle
                              row={row}
                              pendingId={pendingId}
                              onToggle={toggleSuspend}
                            />
                          </TableCell>

                          <TableCell>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openDatabaseDetails(row)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                              >
                                <Eye size={14} />
                                Databases
                              </button>

                              <button
                                type="button"
                                onClick={() => goEdit(row)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                              >
                                <Edit3 size={14} />
                                Edit
                              </button>
                            </div>
                          </TableCell>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-14 text-center text-slate-500"
                      >
                        <Database
                          size={32}
                          className="mx-auto mb-3 text-slate-300"
                        />

                        <p className="font-medium">
                          {search
                            ? "No matching tenants found."
                            : "No tenant records available."}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {filtered.length ? first + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-slate-700">
                {Math.min(first + PER_PAGE, filtered.length)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {filtered.length}
              </span>{" "}
              tenants
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setPage(Math.max(1, currentPage - 1))
                }
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <span className="rounded-lg bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() =>
                  setPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </main>
      </div>

      <DatabaseDetailsModal
        tenant={selectedTenant}
        details={tenantDetails}
        loading={detailsLoading}
        error={detailsError}
        onClose={() => {
          setSelectedTenant(null);
          setTenantDetails(null);
          setDetailsError("");
        }}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  const colors = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div>
        <p className="text-xs font-medium text-slate-500">
          {label}
        </p>
        <p className="mt-1 text-2xl font-bold text-slate-900">
          {value}
        </p>
      </div>

      <div
        className={`flex h-11 w-11 items-center justify-center rounded-xl ${
          colors[color] || colors.slate
        }`}
      >
        <Icon size={21} />
      </div>
    </div>
  );
}

function TableHead({ children }) {
  return (
    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
      {children}
    </th>
  );
}

function TableCell({ children, className = "" }) {
  return (
    <td
      className={`whitespace-nowrap px-4 py-3 text-slate-700 ${className}`}
    >
      {children}
    </td>
  );
}
